// Try to import Puter.js - handle gracefully if not available
let Puter: any = null;
try {
  // @ts-ignore
  Puter = require('puter');
} catch (error) {
  console.warn('Puter.js not available, using fallback image generation');
}

// Initialize Puter.js client if available
const puter = Puter ? new Puter() : null;

export interface GeneratedImage {
  id: string;
  url: string;
  prompt: string;
  timestamp: Date;
  style: string;
  altText: string;
  metadata: {
    width: number;
    height: number;
    model: string;
    generationTime: number;
  };
}

export interface ImageGenerationOptions {
  prompt: string;
  style?: 'realistic' | 'illustrated' | 'minimalist' | 'professional' | 'futuristic';
  size?: 'square' | 'portrait_4_3' | 'landscape_16_9' | 'square_hd';
  quality?: 'standard' | 'high';
  branding?: boolean;
  context?: string;
}

export interface GenerationCache {
  [key: string]: {
    image: GeneratedImage;
    expires: number;
  };
}

export class ImageGenerationService {
  private cache: GenerationCache = {};
  private readonly CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours
  private readonly RATE_LIMIT_DELAY = 2000; // 2 seconds between requests
  private lastRequestTime = 0;
  // private requestQueue: (() => void)[] = []; // Queue for rate limiting (future implementation)

  constructor() {
    this.initializeService();
  }

  private async initializeService() {
    try {
      // Initialize Puter.js with API key if available
      const apiKey = import.meta.env.VITE_PUTER_API_KEY;
      if (apiKey) {
        // Configure with API key for production use
        console.log('Puter.js initialized with API key');
      } else {
        console.log('Puter.js initialized for development');
      }
    } catch (error) {
      console.error('Failed to initialize Puter.js:', error);
    }
  }

  private generateCacheKey(options: ImageGenerationOptions): string {
    return JSON.stringify({
      prompt: options.prompt,
      style: options.style || 'realistic',
      size: options.size || 'landscape_16_9',
      quality: options.quality || 'high',
      branding: options.branding !== false
    });
  }

  private isCacheValid(cacheKey: string): boolean {
    const cached = this.cache[cacheKey];
    if (!cached) return false;
    
    return Date.now() < cached.expires;
  }

  private async enforceRateLimit(): Promise<void> {
    const now = Date.now();
    const timeSinceLastRequest = now - this.lastRequestTime;
    
    if (timeSinceLastRequest < this.RATE_LIMIT_DELAY) {
      const delay = this.RATE_LIMIT_DELAY - timeSinceLastRequest;
      await new Promise(resolve => setTimeout(resolve, delay));
    }
    
    this.lastRequestTime = Date.now();
  }

  private enhancePrompt(options: ImageGenerationOptions): string {
    let enhancedPrompt = options.prompt;
    
    // Add Biotune branding context
    if (options.branding !== false) {
      enhancedPrompt += ", Biotune health technology, modern medical aesthetics, clean professional design";
    }
    
    // Add style-specific enhancements
    switch (options.style) {
      case 'realistic':
        enhancedPrompt += ", photorealistic, high quality, professional photography";
        break;
      case 'illustrated':
        enhancedPrompt += ", digital illustration, clean vector art, modern design";
        break;
      case 'minimalist':
        enhancedPrompt += ", minimalist design, clean lines, simple elegance";
        break;
      case 'professional':
        enhancedPrompt += ", professional corporate style, high-end design, premium quality";
        break;
      case 'futuristic':
        enhancedPrompt += ", futuristic design, advanced technology aesthetic, innovative";
        break;
    }
    
    // Add context-specific enhancements
    if (options.context === 'hero') {
      enhancedPrompt += ", hero section background, website header, banner image";
    } else if (options.context === 'dashboard') {
      enhancedPrompt += ", dashboard interface, user interface, app screenshot";
    } else if (options.context === 'device') {
      enhancedPrompt += ", mobile app interface, wearable device, health tracking";
    }
    
    return enhancedPrompt;
  }

  public async generateImage(options: ImageGenerationOptions): Promise<GeneratedImage> {
    try {
      // Check cache first
      const cacheKey = this.generateCacheKey(options);
      if (this.isCacheValid(cacheKey)) {
        console.log('Returning cached image');
        return this.cache[cacheKey].image;
      }

      // Enforce rate limiting
      await this.enforceRateLimit();

      // Enhance the prompt
      const enhancedPrompt = this.enhancePrompt(options);
      
      console.log('Attempting image generation with prompt:', enhancedPrompt);
      
      let result: { url: string; metadata: any } | null = null;

      // Try multiple image generation approaches
      
      // 1. Try Puter.js if available
      if (puter && puter.ai && puter.ai.img2img) {
        try {
          console.log('Trying Puter.js image generation...');
          result = await puter.ai.img2img({
            prompt: enhancedPrompt,
            width: options.size === 'portrait_4_3' ? 1024 : 1920,
            height: options.size === 'portrait_4_3' ? 1365 : 1080,
            steps: options.quality === 'high' ? 50 : 30,
            guidance_scale: 7.5,
            negative_prompt: "low quality, blurry, distorted, amateur, unprofessional"
          });
          console.log('Puter.js generation successful');
        } catch (puterError) {
          console.warn('Puter.js generation failed:', puterError);
        }
      }

      // 2. Try external image APIs if Puter.js failed or unavailable
      if (!result) {
        try {
          console.log('Trying external image generation APIs...');
          
          // Try Unsplash API for free images - use simpler queries to avoid failures
          // const unsplashKeywords = [
          //   'health', 'wellness', 'fitness', 'nutrition', 'medical', 'technology',
          //   'ecosystem', 'integration', 'dashboard', 'analytics'
          // ];
          
          // Extract keywords from the prompt
          // const promptWords = enhancedPrompt.toLowerCase().split(/\s+/);
          // const matchedKeywords = unsplashKeywords.filter(keyword => 
          //   promptWords.some(word => word.includes(keyword) || keyword.includes(word))
          // );
          
          // Use matched keywords or fallback to general health terms
          // const searchTerms = matchedKeywords.length > 0 ? matchedKeywords.slice(0, 3) : ['health', 'technology'];
          // const unsplashQuery = searchTerms.join(',');
          
          // Try Unsplash API using direct image URLs with proper CORS handling
          // Use Unsplash Source with specific photo IDs that are known to work
          const reliableUnsplashPhotos = [
            'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=1920&h=1080&fit=crop', // Health/tech
            'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=1920&h=1080&fit=crop', // Medical
            'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=1920&h=1080&fit=crop', // Wellness
            'https://images.unsplash.com/photo-1550831106-0994fe8abcfe?w=1920&h=1080&fit=crop', // Fitness
            'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=1920&h=1080&fit=crop'  // Health
          ];
          
          // Try multiple reliable Unsplash photos
          for (const unsplashUrl of reliableUnsplashPhotos) {
            try {
              // Create a test image element to check if URL works
              const testImg = new Image();
              testImg.crossOrigin = 'anonymous'; // Enable CORS
              testImg.src = unsplashUrl;
              
              // Use the URL if it loads successfully (with timeout)
              const loaded = await new Promise((resolve) => {
                testImg.onload = () => resolve(true);
                testImg.onerror = () => resolve(false);
                setTimeout(() => resolve(false), 3000); // 3 second timeout
              });
              
              if (loaded) {
                result = {
                  url: unsplashUrl,
                  metadata: {
                    width: 1920,
                    height: 1080,
                    model: 'unsplash-api',
                    generationTime: Date.now()
                  }
                };
                console.log('Unsplash API generation successful');
                break;
              }
            } catch (urlError) {
              console.warn(`Unsplash URL failed: ${unsplashUrl}`, urlError);
              continue;
            }
          }
        } catch (apiError) {
          console.warn('External API generation failed:', apiError);
        }
      }

      // 3. Use placeholder as final fallback
      if (!result) {
        console.log('Using placeholder fallback');
        // Use a more reliable placeholder service or generate a data URL
        const width = options.size === 'portrait_4_3' ? 1024 : 1920;
        const height = options.size === 'portrait_4_3' ? 1365 : 1080;
        const text = encodeURIComponent(enhancedPrompt.substring(0, 30));
        
        // Try multiple placeholder services in order of reliability
        const placeholderUrls = [
          // Use reliable placeholder services
          `https://via.placeholder.com/${width}x${height}/4F46E5/FFFFFF?text=${text}`,
          `https://placehold.co/${width}x${height}/4F46E5/FFFFFF?text=${text}`,
          `https://dummyimage.com/${width}x${height}/4F46E5/FFFFFF&text=${text}`,
          // Data URL as final fallback - creates a simple gradient SVG
          `data:image/svg+xml;base64,${btoa(`
            <svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" style="stop-color:#4F46E5;stop-opacity:1" />
                  <stop offset="100%" style="stop-color:#7C3AED;stop-opacity:1" />
                </linearGradient>
              </defs>
              <rect width="100%" height="100%" fill="url(#grad)" />
              <text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="white" font-family="Arial, sans-serif" font-size="24">
                ${enhancedPrompt.substring(0, 20)}...
              </text>
            </svg>
          `)}`
        ];
        
        result = {
          url: placeholderUrls[0], // Use the first one, but we have fallbacks
          metadata: {
            width: width,
            height: height,
            model: 'placeholder-fallback',
            generationTime: 0
          }
        };
      }

      if (!result || !result.url) {
        throw new Error('Image generation failed - no URL returned');
      }

      const generatedImage: GeneratedImage = {
        id: `img_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        url: result.url,
        prompt: enhancedPrompt,
        timestamp: new Date(),
        style: options.style || 'realistic',
        altText: this.generateAltText(options.prompt),
        metadata: {
          width: result.metadata.width || (options.size === 'portrait_4_3' ? 1024 : 1920),
          height: result.metadata.height || (options.size === 'portrait_4_3' ? 1365 : 1080),
          model: result.metadata.model || 'puter-ai',
          generationTime: result.metadata.generationTime || 0
        }
      };

      // Cache the result
      this.cache[cacheKey] = {
        image: generatedImage,
        expires: Date.now() + this.CACHE_DURATION
      };

      console.log('Image generated successfully:', generatedImage.id, 'Model:', generatedImage.metadata.model, 'URL:', generatedImage.url.substring(0, 50) + '...');
      return generatedImage;

    } catch (error) {
      console.error('Image generation error:', error);
      
      // Return fallback image or error image
      return this.getFallbackImage(options);
    }
  }

  private generateAltText(prompt: string): string {
    // Generate descriptive alt text from the prompt
    const cleanPrompt = prompt.replace(/Biotune.*?(?:,|$)/gi, '').trim();
    return `AI-generated image: ${cleanPrompt.charAt(0).toUpperCase() + cleanPrompt.slice(1)}`;
  }

  private getFallbackImage(options: ImageGenerationOptions): GeneratedImage {
    // Return a fallback image URL or placeholder
    const fallbackPrompts = [
      "Modern health technology interface, clean design, blue and white colors",
      "Abstract health and wellness background, gradient design",
      "Medical technology concept, professional healthcare imagery"
    ];
    
    const randomPrompt = fallbackPrompts[Math.floor(Math.random() * fallbackPrompts.length)];
    
    // Use more reliable placeholder services
    const fallbackUrls = [
      `https://via.placeholder.com/1920x1080/4F46E5/FFFFFF?text=Biotune+AI+Image`,
      `https://placehold.co/1920x1080/4F46E5/FFFFFF?text=Biotune+AI+Image`,
      `https://dummyimage.com/1920x1080/4F46E5/FFFFFF&text=Biotune+AI+Image`,
      // Data URL as final fallback - creates a simple gradient SVG
      `data:image/svg+xml;base64,${btoa(`
        <svg width="1920" height="1080" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style="stop-color:#4F46E5;stop-opacity:1" />
              <stop offset="100%" style="stop-color:#7C3AED;stop-opacity:1" />
            </linearGradient>
          </defs>
          <rect width="100%" height="100%" fill="url(#grad)" />
          <text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="white" font-family="Arial, sans-serif" font-size="48">
            Biotune AI Generated Image
          </text>
        </svg>
      `)}`
    ];
    
    return {
      id: `fallback_${Date.now()}`,
      url: fallbackUrls[0], // Use the first reliable option
      prompt: randomPrompt,
      timestamp: new Date(),
      style: options.style || 'realistic',
      altText: 'AI-generated health technology image',
      metadata: {
        width: 1920,
        height: 1080,
        model: 'fallback',
        generationTime: 0
      }
    };
  }

  public async generateBatch(images: ImageGenerationOptions[]): Promise<GeneratedImage[]> {
    const results: GeneratedImage[] = [];
    
    for (const options of images) {
      try {
        const image = await this.generateImage(options);
        results.push(image);
      } catch (error) {
        console.error('Batch generation error for prompt:', options.prompt, error);
        results.push(this.getFallbackImage(options));
      }
    }
    
    return results;
  }

  public clearCache(): void {
    this.cache = {};
    console.log('Image generation cache cleared');
  }

  public getCacheStats(): { totalCached: number; totalSize: number } {
    const entries = Object.keys(this.cache);
    return {
      totalCached: entries.length,
      totalSize: entries.reduce((total, key) => {
        return total + (this.cache[key].image.metadata.width * this.cache[key].image.metadata.height * 4); // Rough estimate
      }, 0)
    };
  }
}

// Create singleton instance
export const imageGenerationService = new ImageGenerationService();

// Export types for use in components
// Export types are already defined at the top of the file